/**
 * TopicLandingPage — cross-country landing for a single dimension/topic.
 *
 * URL: /topics/<dimension-slug>  (EN canonical), /ru/topics/<dimension-slug>.
 *
 * Acts as the funnel page from the home "Ten dimensions of life" cards
 * into the per-country chapters.  The page lists every country with a
 * published article for the bound wiki topic, plus a grid of all 30
 * countries grouped by region so the user can see what is coming.
 *
 * SEO target: long-tail "italy visa", "portugal tax", "italy climate" type
 * queries.  The chapter card text carries the country name + dimension in
 * the headline.
 */

import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useDocumentLang } from '@/hooks/useDocumentLang';
import { useJsonLd, useSeoMeta } from '@/hooks/useSeoMeta';
import { countriesQuery, type Country } from '@/data';
import {
  buildWikiUrl,
  countryCodeToSlug,
  getLangDef,
  type CountryCode,
  type LangCode,
} from '@/features/wiki/architecture';
import {
  buildTopicLandingUrl,
  getTopicLanding,
  isTopicLandingSlug,
  TOPIC_LANDINGS,
} from '@/features/wiki/topics-registry';
import { listPublishedTopics } from '@/features/wiki/content-registry';

interface TopicLandingPageProps {
  lang: LangCode;
}

export default function TopicLandingPage({ lang }: TopicLandingPageProps) {
  useDocumentLang();
  const { t, i18n } = useTranslation();
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    if (i18n.language !== lang) {
      i18n.changeLanguage(lang).catch(() => {});
    }
  }, [lang, i18n]);

  const landing = useMemo(() => (slug && isTopicLandingSlug(slug) ? getTopicLanding(slug) : null), [slug]);

  const { data: countriesFile, isLoading } = useQuery(countriesQuery());
  const countries = useMemo(() => countriesFile?.countries ?? [], [countriesFile?.countries]);

  // Set of country codes that have a published article for this topic in the
  // current language (so a "Published chapter" badge only shows when content
  // actually exists in this lang).
  const publishedCcSet = useMemo(() => {
    if (!landing) return new Set<string>();
    return new Set(
      listPublishedTopics()
        .filter((p) => p.topic === landing.wikiTopic && p.lang === lang)
        .map((p) => p.cc),
    );
  }, [landing, lang]);

  const topicLabel = landing
    ? t(`wiki.topic.${landing.wikiTopic}.label`, landing.slug)
    : '';
  const topicDescription = landing
    ? t(`wiki.topic.${landing.wikiTopic}.description`, '')
    : '';

  const headline = landing
    ? t('topics.landing.headlineFmt', {
        defaultValue: '{{topic}} across 31 countries',
        topic: topicLabel,
      })
    : '';

  const description = landing
    ? t('topics.landing.descriptionFmt', {
        defaultValue:
          'Compare {{topic}} routes country by country. {{published}} chapters published so far; the rest are in the pipeline.',
        topic: topicLabel.toLowerCase(),
        published: publishedCcSet.size,
      })
    : '';

  useSeoMeta({
    title: landing
      ? `${headline} · Lodestar`
      : 'Topics · Lodestar',
    description,
    canonical: landing
      ? buildTopicLandingUrl({ slug: landing.slug as never, lang, origin: 'https://lodestar.place' })
      : 'https://lodestar.place/topics',
    locale: getLangDef(lang).ogLocale,
    ogType: 'website',
    ogImage: landing ? `https://lodestar.place/og/it-${landing.wikiTopic}.png` : undefined,
    siteName: 'Lodestar',
    articleSection: topicLabel,
    articleTags: undefined,
  });

  // hreflang alternates for the landing page itself.
  useEffect(() => {
    if (!landing) return;
    const head = document.head;
    const removeOld = head.querySelectorAll('link[rel="alternate"][data-topics-landing]');
    removeOld.forEach((el) => el.remove());
    for (const l of [{ code: 'en' }, { code: 'ru' }] as const) {
      const link = document.createElement('link');
      link.rel = 'alternate';
      link.hreflang = l.code;
      link.href = buildTopicLandingUrl({
        slug: landing.slug as never,
        lang: l.code,
        origin: 'https://lodestar.place',
      });
      link.dataset.topicsLanding = '1';
      head.appendChild(link);
    }
    const xDefault = document.createElement('link');
    xDefault.rel = 'alternate';
    xDefault.hreflang = 'x-default';
    xDefault.href = buildTopicLandingUrl({
      slug: landing.slug as never,
      lang: 'en',
      origin: 'https://lodestar.place',
    });
    xDefault.dataset.topicsLanding = '1';
    head.appendChild(xDefault);
    return () => {
      head.querySelectorAll('link[rel="alternate"][data-topics-landing]').forEach((el) => el.remove());
    };
  }, [landing, lang]);

  // CollectionPage JSON-LD with an ItemList of the published chapters.
  const jsonLd = useMemo(() => {
    if (!landing || countries.length === 0) return null;
    return JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: headline,
      description,
      url: buildTopicLandingUrl({
        slug: landing.slug as never,
        lang,
        origin: 'https://lodestar.place',
      }),
      inLanguage: lang,
      isPartOf: {
        '@type': 'WebSite',
        name: 'Lodestar',
        url: 'https://lodestar.place',
      },
      mainEntity: {
        '@type': 'ItemList',
        numberOfItems: publishedCcSet.size,
        itemListElement: Array.from(publishedCcSet).map((cc, i) => {
          const country = countries.find((c) => c.code === cc);
          return {
            '@type': 'ListItem',
            position: i + 1,
            url: buildWikiUrl({
              cc: cc as CountryCode,
              topic: landing.wikiTopic,
              lang,
              origin: 'https://lodestar.place',
            }),
            name: country ? (lang === 'ru' ? country.name_ru : country.name_en) : cc,
          };
        }),
      },
    });
  }, [landing, countries, headline, description, publishedCcSet, lang]);
  useJsonLd(jsonLd, 'topic-landing-collection');

  if (!landing) {
    return <TopicLandingNotFound lang={lang} />;
  }
  if (isLoading) {
    return <TopicLandingSkeleton />;
  }

  // Split countries: published first (sorted alphabetically by display name),
  // then the remaining 29 grouped by region.
  const displayName = (c: Country) => (lang === 'ru' ? c.name_ru : c.name_en);
  const publishedCountries = countries
    .filter((c) => publishedCcSet.has(c.code))
    .sort((a, b) => displayName(a).localeCompare(displayName(b), lang));
  const otherCountries = countries
    .filter((c) => !publishedCcSet.has(c.code))
    .sort((a, b) => displayName(a).localeCompare(displayName(b), lang));

  // Group otherCountries by region (preserve appearance order of regions).
  const otherByRegion = new Map<string, Country[]>();
  for (const c of otherCountries) {
    const r = c.region ?? '—';
    if (!otherByRegion.has(r)) otherByRegion.set(r, []);
    otherByRegion.get(r)!.push(c);
  }

  const atlasUrl = buildWikiUrl({ lang });

  return (
    <article className="mx-auto w-full max-w-5xl px-4 py-8 sm:py-12">
      {/* Breadcrumb */}
      <nav
        aria-label="breadcrumb"
        className="mb-5 font-mono text-[10px] uppercase tracking-wider text-ink-400"
      >
        <Link to={lang === 'en' ? '/' : `/${lang}`} className="hover:text-ink-700">
          {t('topics.landing.home', { defaultValue: 'Lodestar' })}
        </Link>
        <span aria-hidden="true" className="mx-1.5">/</span>
        <span className="text-ink-700">
          {t('topics.landing.crumb', { defaultValue: 'Topics' })}
        </span>
        <span aria-hidden="true" className="mx-1.5">/</span>
        <span className="text-ink-700">{topicLabel}</span>
      </nav>

      {/* Hero */}
      <header className="mb-10 sm:mb-12">
        <p className="font-mono text-[11px] uppercase tracking-wider text-ink-500">
          <span className="mr-2 text-base" aria-hidden="true">{landing.icon}</span>
          {landing.source}
        </p>
        <h1 className="mt-3 font-display text-[clamp(28px,6vw,44px)] font-medium italic tracking-tight text-ink-900">
          {headline}
        </h1>
        <p className="mt-4 max-w-2xl text-base text-ink-700 sm:text-lg sm:leading-relaxed">
          {description}
        </p>
        {topicDescription && (
          <p className="mt-2 max-w-2xl text-sm text-ink-500">{topicDescription}</p>
        )}
      </header>

      {/* Published chapters */}
      <section aria-labelledby="published-title" className="mb-12">
        <h2
          id="published-title"
          className="mb-4 font-display text-xl font-medium italic tracking-tight text-ink-900 sm:text-2xl"
        >
          {t('topics.landing.published', { defaultValue: 'Published chapters' })}
          <span className="ml-2 font-mono text-xs uppercase tracking-wider text-ink-400">
            · {publishedCountries.length}
          </span>
        </h2>
        {publishedCountries.length === 0 ? (
          <p className="rounded-lg border border-dashed border-ink-100 bg-paper-2/60 px-4 py-3 text-sm text-ink-500">
            {t('topics.landing.publishedEmpty', {
              defaultValue: 'No country chapters yet. Italy is the editorial flagship — chapters land here as they ship.',
            })}
          </p>
        ) : (
          <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {publishedCountries.map((c) => (
              <li key={c.code}>
                <Link
                  to={buildWikiUrl({ cc: c.code as CountryCode, topic: landing.wikiTopic, lang })}
                  className="flex h-full flex-col rounded-lg border border-ink-100 bg-paper-2/60 p-4 transition-all duration-150 hover:-translate-y-0.5 hover:border-terra-600/40 hover:bg-paper-2 hover:shadow-sm motion-reduce:hover:translate-y-0"
                  data-testid={`topic-published-${c.code}`}
                >
                  <p className="font-mono text-[10px] uppercase tracking-wider text-ink-500">
                    <span className="mr-1.5">{c.flag_emoji}</span>
                    {c.region ?? ''}
                  </p>
                  <h3 className="mt-1.5 font-display text-lg font-medium italic tracking-tight text-ink-900">
                    {displayName(c)} · {topicLabel}
                  </h3>
                  <p className="mt-2 text-sm text-ink-500">
                    {t('topics.landing.readChapter', { defaultValue: 'Read the chapter' })} →
                  </p>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Coming soon, grouped by region */}
      <section aria-labelledby="coming-title" className="mb-12">
        <h2
          id="coming-title"
          className="mb-4 font-display text-xl font-medium italic tracking-tight text-ink-900 sm:text-2xl"
        >
          {t('topics.landing.coming', { defaultValue: 'Coming next' })}
          <span className="ml-2 font-mono text-xs uppercase tracking-wider text-ink-400">
            · {otherCountries.length}
          </span>
        </h2>
        <div className="space-y-6">
          {Array.from(otherByRegion.entries()).map(([region, list]) => (
            <div key={region}>
              <h3 className="mb-2 font-mono text-[11px] uppercase tracking-wider text-ink-500">
                {region}
              </h3>
              <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
                {list.map((c) => {
                  const ccSlug = countryCodeToSlug(c.code);
                  const hubUrl = ccSlug ? buildWikiUrl({ cc: c.code as CountryCode, lang }) : null;
                  return (
                    <li key={c.code}>
                      {hubUrl ? (
                        <Link
                          to={hubUrl}
                          className="flex items-center gap-2 rounded-md border border-ink-100/60 bg-paper px-3 py-2 text-sm text-ink-700 transition-colors hover:border-ink-300 hover:bg-paper-2"
                        >
                          <span className="text-base" aria-hidden="true">{c.flag_emoji}</span>
                          <span>{displayName(c)}</span>
                        </Link>
                      ) : (
                        <span className="flex items-center gap-2 rounded-md border border-ink-100/60 bg-paper-2/40 px-3 py-2 text-sm text-ink-400">
                          <span className="text-base" aria-hidden="true">{c.flag_emoji}</span>
                          <span>{displayName(c)}</span>
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* Sibling topics navigator */}
      <section aria-labelledby="sibling-title" className="mb-12 border-t border-ink-100 pt-8">
        <h2
          id="sibling-title"
          className="mb-4 font-display text-lg font-medium italic tracking-tight text-ink-900"
        >
          {t('topics.landing.others', { defaultValue: 'Other dimensions' })}
        </h2>
        <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
          {TOPIC_LANDINGS.filter((tl) => tl.slug !== landing.slug).map((tl) => (
            <li key={tl.slug}>
              <Link
                to={buildTopicLandingUrl({ slug: tl.slug as never, lang })}
                className="flex items-center gap-2 rounded-md border border-ink-100 bg-paper-2/60 px-3 py-2 text-sm text-ink-700 transition-colors hover:bg-paper-2"
              >
                <span className="text-base" aria-hidden="true">{tl.icon}</span>
                <span>{t(`wiki.topic.${tl.wikiTopic}.label`, tl.slug)}</span>
              </Link>
            </li>
          ))}
        </ul>
      </section>

      {/* CTA */}
      <section className="mt-12 border-t border-ink-100 pt-6">
        <div className="flex flex-wrap gap-3">
          <Link
            to={lang === 'en' ? '/quiz' : `/quiz?lang=${lang}`}
            className="inline-flex h-11 items-center rounded-md bg-terra-600 px-5 text-sm font-medium text-paper transition-colors hover:bg-terra-700"
          >
            {t('topics.landing.ctaQuiz', { defaultValue: 'Take the quiz →' })}
          </Link>
          <Link
            to={atlasUrl}
            className="inline-flex h-11 items-center rounded-md border border-ink-100 px-5 text-sm font-medium text-ink-700 transition-colors hover:bg-paper-2"
          >
            {t('topics.landing.ctaAtlas', { defaultValue: 'Browse the atlas →' })}
          </Link>
        </div>
      </section>
    </article>
  );
}

/* ────────────────────────────────────────────────────────────────── */

function TopicLandingSkeleton() {
  return (
    <div aria-hidden="true" className="mx-auto w-full max-w-5xl animate-pulse px-4 py-12">
      <div className="mb-4 h-3 w-32 rounded bg-ink-100" />
      <div className="mb-3 h-10 w-3/4 rounded bg-ink-100" />
      <div className="mb-8 h-4 w-2/3 rounded bg-ink-100" />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-32 rounded-lg bg-ink-100" />
        ))}
      </div>
    </div>
  );
}

function TopicLandingNotFound({ lang }: { lang: LangCode }) {
  const { t } = useTranslation();
  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col items-center px-4 py-20 text-center">
      <h1 className="font-display text-3xl font-medium italic tracking-tight text-ink-900">
        {t('topics.landing.notFoundTitle', { defaultValue: 'Topic not found' })}
      </h1>
      <p className="mt-3 text-sm text-ink-500">
        {t('topics.landing.notFoundSub', {
          defaultValue: 'Pick a dimension from the home page to see all chapters and countries.',
        })}
      </p>
      <Link
        to={lang === 'en' ? '/' : `/${lang}`}
        className="mt-6 inline-flex h-11 items-center rounded-md bg-terra-600 px-5 text-sm font-medium text-paper hover:bg-terra-700"
      >
        {t('topics.landing.notFoundCta', { defaultValue: 'Back to home' })}
      </Link>
    </div>
  );
}
